import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";
import { MovieState } from "../movieState";
// Animation
import { motion } from "framer-motion";
import { pageAnimation } from "../animation";
import { primaryColor } from "../components/GlobalStyle";
import ErrorPage from "./ErrorPage";

const MovieDetail = () => {
  const location = useLocation();
  const url = location.pathname;
  const [movies] = useState(MovieState);
  const [movie, setMovie] = useState(null);

  useEffect(() => {
    const currentMovie = movies.filter((stateMovie) => stateMovie.url === url);
    setMovie(currentMovie[0]);
  }, [movies, url]);

  return (
    <>
      {movie ? (
        <Details
          variants={pageAnimation}
          initial="hidden"
          animate="show"
          exit="exit"
        >
          <HeadLine>
            <TitleLink
              href={movie.websiteUrl || "/"}
              target={movie.websiteUrl ? "_blank" : "_self"}
              rel={movie.websiteUrl ? "noopener noreferrer" : ""}
            >
              <h2>{movie.title}</h2>
              <LinkIcon>↗</LinkIcon>
            </TitleLink>
          </HeadLine>
          <Awards>
            {movie.awards.map((award) => (
              <Award
                title={award.title}
                description={award.description}
                key={award.title}
              />
            ))}
          </Awards>
        </Details>
      ) : (
        <ErrorPage />
      )}
    </>
  );
};

const Details = styled(motion.div)`
  color: #fff;

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 1rem;
  }
`;

const HeadLine = styled.div`
  position: relative;

  h2 {
    position: absolute;
    top: 10%;
    left: 50%;
    transform: translate(-50%, -10%);
  }

  img {
    width: 100%;
    height: 70vh;
    object-fit: cover;
  }

  @media (max-width: 768px) {
    text-align: center;
    margin-bottom: 3rem;
    position: static;

    h2 {
      position: static;
      transform: none;
      font-size: 3rem;
      margin-bottom: 2rem;
    }

    img {
      display: none;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;

    h2 {
      font-size: 2.5rem;
    }
  }
`;

const TitleLink = styled.a`
  text-decoration: none;
  color: inherit;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  position: absolute;
  top: 10%;
  left: 50%;
  transform: translate(-50%, -10%);
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: ${primaryColor};
    transform: translate(-50%, -10%) scale(1.03);
  }

  h2 {
    position: relative;
    top: auto;
    left: auto;
    transform: none;
    margin: 0;
    display: inline-block;
  }

  @media (max-width: 768px) {
    position: static;
    transform: none;
    justify-content: center;

    &:hover {
      transform: scale(1.03);
    }

    h2 {
      margin: 0;
    }
  }
`;

const LinkIcon = styled.span`
  font-size: 2rem;
  opacity: 0.8;
  transition: all 0.3s ease;
  margin-left: 0.8rem;
  display: inline-flex;
  align-items: center;

  ${TitleLink}:hover & {
    opacity: 1;
    transform: translateY(-2px);
  }
`;

const Awards = styled.div`
  min-height: 80vh;
  display: flex;
  margin: 5rem 10rem;
  justify-content: space-around;
  align-items: center;

  @media (max-width: 1300px) {
    margin: 3rem 5rem;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    margin: 2rem;
    min-height: auto;
  }

  @media (max-width: 480px) {
    margin: 1rem;
  }
`;

const AwardStyle = styled.div`
  padding: 5rem;

  h3 {
    font-size: 2rem;
  }

  .line {
    width: 100%;
    background: ${primaryColor};
    height: 0.5rem;
    margin: 1rem 0rem;
  }

  p {
    padding: 2rem 0;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    text-align: center;
    margin-bottom: 2rem;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);

    h3 {
      font-size: 1.8rem;
      color: white;
      margin-bottom: 1rem;
    }

    p {
      padding: 1rem 0;
      line-height: 1.6;
      color: #cccccc;
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    margin-bottom: 1.5rem;

    h3 {
      font-size: 1.5rem;
    }

    p {
      font-size: 0.9rem;
    }
  }
`;

// Award Component
const Award = ({ title, description }) => {
  return (
    <AwardStyle>
      <h3>{title}</h3>
      <div className="line"></div>
      <p>{description}</p>
    </AwardStyle>
  );
};

export default MovieDetail;
